/**
 * useGeocode - Hook for geocoding zip codes to coordinates
 *
 * Automatically geocodes zip codes when they change and updates the tariff location.
 * Uses API with fallback to hardcoded common zip codes.
 *
 * @module hooks/useGeocode
 */

import { useEffect } from 'react';
import { debug } from '../utils/debug';
import { API_BASE_URL } from '../services/api';

/**
 * Common zip code fallbacks for when API fails
 * Only used as last resort - prefer API geocoding
 */
const ZIP_FALLBACK = {
    "90277": { lat: 33.8358, lon: -118.3877 }, // Redondo Beach, CA
    "90245": { lat: 33.9192, lon: -118.4165 }, // El Segundo, CA
    "94105": { lat: 37.7859, lon: -122.3887 }, // San Francisco, CA
    "10001": { lat: 40.7484, lon: -73.9967 }, // New York, NY
    "90210": { lat: 34.0901, lon: -118.4065 }, // Beverly Hills, CA
};

/**
 * @typedef {Object} GeocodeOptions
 * @property {string} zipCode - The zip code to geocode
 * @property {Object|null} currentLocation - Current tariff location (if already set)
 * @property {Function} setTariffLocation - Function to update tariff location
 * @property {Function} setSnackbar - Function to show snackbar messages
 */

/**
 * Hook for geocoding zip codes
 *
 * Automatically geocodes when zipCode changes and tariffLocation is not set.
 * Shows warning if geocoding fails for unknown zip codes.
 *
 * @param {GeocodeOptions} options
 */
export function useGeocode({ zipCode, currentLocation, setTariffLocation, setSnackbar }) {
    useEffect(() => {
        const geocodeZip = async () => {
            const zip = zipCode?.trim();
            if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) return;

            // Skip if tariffLocation is already set with valid coords (from tariff selection)
            if (currentLocation?.lat && currentLocation?.lon) {
                return;
            }

            // Try API first
            try {
                const response = await fetch(`${API_BASE_URL}/api/tariffs?address=${encodeURIComponent(zip)}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.location?.lat && data.location?.lon) {
                        debug.log(`[useGeocode] Zip ${zip} -> API:`, data.location);
                        setTariffLocation(data.location);
                        return;
                    }
                }
            } catch (err) {
                debug.warn(`[useGeocode] API failed for ${zip}:`, err);
            }

            // Use fallback only if API fails - but log warning that this is not ideal
            // Note: Do NOT silently fall back to LA - show clear message to user
            if (ZIP_FALLBACK[zip]) {
                debug.warn(`[useGeocode] Using hardcoded fallback for zip ${zip}. API geocoding failed.`);
                setTariffLocation(ZIP_FALLBACK[zip]);
            } else {
                // Set empty location and show warning - don't silently use LA
                debug.error(`[useGeocode] No geocode for ${zip} - user must enter location manually or use valid zip`);
                setTariffLocation(null);
                setSnackbar({
                    open: true,
                    message: `Could not geocode zip code ${zip}. Please verify the zip code or enter a full address.`,
                    severity: 'warning',
                });
            }
        };

        geocodeZip();
    }, [zipCode, currentLocation?.lat, currentLocation?.lon, setTariffLocation, setSnackbar]);
}

export default useGeocode;
